import { useState, useEffect } from "react";
import React from "react";
import { Route, Routes, useLocation } from 'react-router-dom';
import axios from "axios";
import '../index.css';

import SignIn from './auth/SignIn';
import toast, { Toaster } from 'react-hot-toast';
import { InlineSpinner } from './loading';

import PdfSign from "./PdfSign";
import CreateOrg from "./CreateOrg";
import OrgsBoard from "./OrgsBoard";
import Org from "./Org";
import Bundle from "./Bundle";
import CreateTemplate from "./CreateNewTemplate";
import SentPage from './SentPage';
import Submitted from './Submitted';
import UpdateTemplate from './UpdateTemplate';
import NavBar from './NavBar';
import AIFieldDetectionPOC from './AIFieldDetectionPOC';
import ErrorBoundary from './ErrorBoundary';

const OTP_API = 'https://document-fill-backend-869166735.us-central1.run.app/api/auth';

// Public routes where NavBar should NOT appear (parent-facing pages)
const PUBLIC_ROUTE_PREFIXES = ['/template/', '/bundle/', '/success/'];

export default function Main(props) {
  const [otp, setOtp] = useState("");
  const [ph, setPh] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [user, setUser] = useState(null);
  const [manager, setManager] = useState(false);
  const location = useLocation();

  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some(prefix => location.pathname.startsWith(prefix));

  // Restore session from localStorage or auto-auth via token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      setLoading(true);
      axios.get(`${OTP_API}/verify-token`, { params: { token } })
        .then(res => {
          const authUser = { phoneNumber: res.data.phone };
          setUser(authUser);
          localStorage.setItem('authUser', JSON.stringify(authUser));
          // Strip token from URL so refresh doesn't re-use the one-time token
          params.delete('token');
          const remaining = params.toString();
          const newUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
          window.history.replaceState({}, '', newUrl);
        })
        .catch(() => {
          // Token invalid/expired — fall through to normal login
        })
        .finally(() => setLoading(false));
    } else {
      const saved = localStorage.getItem('authUser');
      if (saved) {
        try {
          setUser(JSON.parse(saved));
        } catch (e) {
          localStorage.removeItem('authUser');
        }
      }
    }
  }, []);

  function handleSignOut() {
    setUser(null);
    localStorage.removeItem('authUser');
  }

  async function onSignup() {
    setLoading(true);

    const isDevelopment = process.env.NODE_ENV === 'development';
    const shouldBypass = isDevelopment && process.env.REACT_APP_BYPASS_AUTH === 'true';

    if (shouldBypass) {
      setLoading(false);
      setShowOTP(true);
      toast.success('🔧 DEV MODE: קוד אימות נשלח (bypass)');
      return;
    }

    try {
      const res = await fetch(`${OTP_API}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: ph }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) throw new Error('הגעת למגבלת SMS היומית, נסה שוב מחר');
        throw new Error(data.error || 'שגיאה בשליחת הקוד');
      }
      setShowOTP(true);
    } catch (error) {
      toast.error(error.message || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  }

  async function onOTPVerify() {
    setLoading(true);

    const isDevelopment = process.env.NODE_ENV === 'development';
    const shouldBypass = isDevelopment && process.env.REACT_APP_BYPASS_AUTH === 'true';

    if (shouldBypass) {
      const mockUser = { phoneNumber: '0548004990' };
      setUser(mockUser);
      localStorage.setItem('authUser', JSON.stringify(mockUser));
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${OTP_API}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: ph, code: otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) throw new Error('הגעת למגבלת SMS היומית, נסה שוב מחר');
        if (data.attemptsLeft !== undefined) {
          throw new Error(`${data.error} — נותרו ${data.attemptsLeft} נסיונות`);
        }
        throw new Error(data.error || 'הקוד שגוי');
      }
      const authUser = { phoneNumber: data.phone };
      setUser(authUser);
      localStorage.setItem('authUser', JSON.stringify(authUser));
    } catch (error) {
      toast.error(error.message || 'שגיאה באימות');
    } finally {
      setLoading(false);
    }
  }

  const signInProps = {
    onClick: onSignup,
    ph, setPh,
    otp, setOtp,
    showOTP, setShowOTP,
    onOTPVerify,
    setManager,
  };

  return (
    <div className="bg-light" style={{ height: '100%' }}>
      {loading && (
        <div className="loading-wrapper">
          <div className="loading">
            <InlineSpinner size="xl" />
          </div>
        </div>
      )}
      {user && <NavBar user={user.phoneNumber} signOut={handleSignOut} publicRoute={isPublicRoute} />}

      <Routes>

        <Route path="/create-org" element={user ? (
          <ErrorBoundary><CreateOrg /></ErrorBoundary>
        ) : (
          <>
            <Toaster toastOptions={{ duration: 4000 }} containerStyle={{ zIndex: 99999 }} />
            <SignIn {...signInProps} />
          </>
        )} />

        <Route path="/" element={user ? (
          <ErrorBoundary><OrgsBoard phoneNumber={user.phoneNumber} /></ErrorBoundary>
        ) : (
          <>
            <Toaster toastOptions={{ duration: 4000 }} />
            <SignIn {...signInProps} />
          </>
        )} />

        <Route path="/org/:key" element={user ? (
          <ErrorBoundary><Org user={user.phoneNumber} /></ErrorBoundary>
        ) : (
          <>
            <Toaster toastOptions={{ duration: 4000 }} />
            <SignIn {...signInProps} />
          </>
        )} />

        <Route path="/submitted/:key" element={user ? (
          <ErrorBoundary><Submitted /></ErrorBoundary>
        ) : (
          <>
            <Toaster toastOptions={{ duration: 4000 }} />
            <SignIn {...signInProps} />
          </>
        )} />

        <Route path="/create-template/:key" element={user ? (
          <ErrorBoundary><CreateTemplate /></ErrorBoundary>
        ) : (
          <>
            <Toaster toastOptions={{ duration: 4000 }} />
            <SignIn {...signInProps} />
          </>
        )} />

        <Route path="/update-doc/:key" element={user ? (
          <ErrorBoundary><UpdateTemplate /></ErrorBoundary>
        ) : (
          <>
            <Toaster toastOptions={{ duration: 4000 }} />
            <SignIn {...signInProps} />
          </>
        )} />

        <Route path="/template/:key" element={<ErrorBoundary><PdfSign /></ErrorBoundary>} />
        <Route path="/bundle/:key" element={<ErrorBoundary><Bundle /></ErrorBoundary>} />
        <Route path="/success/:key" element={<ErrorBoundary><SentPage /></ErrorBoundary>} />
        <Route path="/ai-poc" element={<AIFieldDetectionPOC />} />

      </Routes>
    </div>
  );
}
